import React, { useEffect, useRef } from "react";
import { useSetRecoilState } from "recoil";
import { IoFlash } from "react-icons/io5";

import { useUser } from "MVVM/Hooks/useUser";
import { useTrialCardStatus } from "MVVM/Hooks/useTrialCardStatus";
import { StripePaystationState } from "MVVM/State";
import { analytics } from "MVVM/Analytics";
import type { ClipStatusTimeline } from "../../@types";
import { HAPPY_PATH_EVENTS } from "../../@types";
import { TrialButton } from "./SkipTheWaitCTA";

type KeepTheSpeedCTAProps = {
  timeline: ClipStatusTimeline;
  activeIdx: number;
  isStale: boolean;
  isError: boolean;
};

// Add-card upsell for no-card trial users watching their clip move through the
// priority queue — the speed on screen is what a card keeps. Renders in the
// same slot as SkipTheWaitCTA, but the gates are disjoint (FREE vs trialing),
// so the two never coexist.
export const KeepTheSpeedCTA = ({
  timeline,
  activeIdx,
  isStale,
  isError,
}: KeepTheSpeedCTAProps) => {
  const { allstarUser } = useUser();
  const { status: trialCardStatus } = useTrialCardStatus();
  const setStripePaystation = useSetRecoilState(StripePaystationState);

  const isNoCardTrial =
    !!allstarUser?.user?.userGroup?.isTrialPeriod &&
    !!trialCardStatus?.trialing &&
    !trialCardStatus.hasCard;
  const activeEvent = activeIdx >= 0 ? HAPPY_PATH_EVENTS[activeIdx] : undefined;
  const inProgress = activeEvent !== undefined && activeEvent !== "COMPLETED";
  const visible = isNoCardTrial && !isError && (inProgress || isStale);

  const trackingProps = {
    trackId: timeline.trackId,
    game: timeline.game,
    contentType: timeline.contentType,
    queueName: timeline.queueName,
    clipState: activeEvent,
    stale: isStale,
  };

  const shownTrackIds = useRef<Set<string>>(new Set());
  useEffect(() => {
    if (!visible || shownTrackIds.current.has(timeline.trackId)) return;
    shownTrackIds.current.add(timeline.trackId);
    analytics.track("Clip Status Add Card CTA Shown", {
      trackId: timeline.trackId,
      game: timeline.game,
      contentType: timeline.contentType,
      queueName: timeline.queueName,
      clipState: activeEvent,
      stale: isStale,
    });
  }, [visible, activeEvent, isStale, timeline]);

  if (!visible) return null;

  const openAddCard = () => {
    analytics.track("Trial Add Card Clicked", {
      location: "Clip Status Card",
      ...trackingProps,
    });
    setStripePaystation({
      isModalOpen: true,
      type: "subscribe",
      pendingEmail: false,
      shouldRefreshMtx: false,
      trialCardSetup: true,
    });
  };

  return (
    <TrialButton
      onClick={openAddCard}
      title={
        trialCardStatus?.extended
          ? "Add a card to keep Unlimited"
          : "Add a card to keep Unlimited · +7 bonus trial days"
      }
      type="button"
    >
      <IoFlash size={14} />
      Add a card to keep this speed
    </TrialButton>
  );
};
